import React, { useEffect, useRef, useState } from 'react';
import { View, TouchableOpacity, LayoutChangeEvent, StyleProp, TextStyle, ViewStyle } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { subtitleFontSize, smallestMargin } from '_utils/sizes';
import { useThemeContext } from '_utils/themeContext';
import HeimeText from './HeimeText';
import Icon from './Icon/Icon';

const animationDuration = 300;

type AccordionProps = {
    title: string;
    children: React.ReactNode;
    titleStyle?: StyleProp<TextStyle>;
    containerStyle?: StyleProp<ViewStyle>;

    open: boolean;
    onChange: (open: boolean) => void;
};

const Accordion = ({ title, children, titleStyle, containerStyle, open, onChange }: AccordionProps) => {
    const { theme } = useThemeContext();
    const [contentHeight, setContentHeight] = useState(0);
    const animatedHeight = useSharedValue(0);
    const contentRef = useRef<View>(null);

    const animatedStyles = useAnimatedStyle(() => {
        return {
            height: animatedHeight.value,
            overflow: 'hidden',
        };
    });

    const toggleAccordion = () => {
        onChange(!open);
    };

    const onContentLayout = (event: LayoutChangeEvent) => {
        // Measure the content height only once
        const { height } = event.nativeEvent.layout;
        if (Math.abs(contentHeight - height) > 10) {
            setContentHeight(height);
        }
        if (open) {
            animatedHeight.value = height; // If initially open, set to full height
        }
    };

    useEffect(() => {
        if (open) {
            animatedHeight.value = withTiming(contentHeight, { duration: animationDuration });
        } else {
            animatedHeight.value = withTiming(0, { duration: animationDuration });
        }
    }, [animatedHeight, contentHeight, open]);

    return (
        <View style={containerStyle}>
            <TouchableOpacity
                style={{ flexDirection: 'row', justifyContent: 'space-between' }}
                onPress={toggleAccordion}
            >
                <HeimeText
                    style={[
                        {
                            fontSize: subtitleFontSize,
                            color: theme.main,
                            fontWeight: 'bold',
                        },
                        titleStyle,
                    ]}
                >
                    {title}
                </HeimeText>
                <Chevron open={open} />
            </TouchableOpacity>
            <Animated.View style={animatedStyles}>
                <View
                    onLayout={onContentLayout}
                    ref={contentRef}
                    style={[
                        contentHeight === 0 ? { position: 'absolute' } : {},
                        {
                            paddingTop: smallestMargin,
                            gap: smallestMargin,
                        },
                    ]}
                >
                    {children}
                </View>
            </Animated.View>
        </View>
    );
};

const openRotation = -90;
const closedRotation = 90;
const Chevron = ({ open }: { open: boolean }) => {
    const rotation = useSharedValue(open ? openRotation : closedRotation);

    const animatedStyles = useAnimatedStyle(() => {
        return {
            transform: [{ rotate: `${rotation.value}deg` }],
        };
    });

    useEffect(() => {
        if (open) {
            rotation.value = withTiming(openRotation, { duration: animationDuration });
        } else {
            rotation.value = withTiming(closedRotation, { duration: animationDuration });
        }
    }, [rotation, open]);

    return (
        <Animated.View style={animatedStyles}>
            <Icon name="chevron" color="main" />
        </Animated.View>
    );
};

export default Accordion;
